'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { ChevronRight } from 'lucide-react'
import { motion, AnimatePresence, easeOut } from 'framer-motion'

export const HeaderNav = ({ data }: any) => {
  const pathname = usePathname()
  const { language } = useLanguage()
  const isArabic = language === 'ar'
  const isQuotePage = pathname === '/get-a-quote'

  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null)
  const [hoveredSectionIndex, setHoveredSectionIndex] = useState(0)
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null)
  const [hoveredSubIndex, setHoveredSubIndex] = useState<number | null>(null)
  const [hoveredSubSubIndex, setHoveredSubSubIndex] = useState<number | null>(null)

  const resetHoverMenu = () => {
    setActiveMenuIndex(null)
    setHoveredSectionIndex(0)
    setHoveredSubIndex(null)
    setHoveredSubSubIndex(null)
  }

  useEffect(() => {
    const handleScroll = () => {
      if (activeMenuIndex !== null) {
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          setCloseTimeout(null)
        }
        resetHoverMenu()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
    }
  }, [activeMenuIndex, closeTimeout])

  const handleNavItemEnter = (index: number, hasHoverMenu: boolean) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout)
      setCloseTimeout(null)
    }

    if (hasHoverMenu) {
      setActiveMenuIndex(index)
      setHoveredSectionIndex(0)
      setHoveredSubIndex(null)
      setHoveredSubSubIndex(null)
    } else {
      resetHoverMenu()
    }
  }

  const handleNavLeave = () => {
    const timeout = setTimeout(() => {
      resetHoverMenu()
    }, 150)
    setCloseTimeout(timeout)
  }

  const handleMenuEnter = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout)
      setCloseTimeout(null)
    }
  }

  const handleMenuLeave = () => {
    resetHoverMenu()
  }

  const getCurrentDisplayContent = () => {
    if (activeMenuIndex === null) return {}

    const activeItem = data[activeMenuIndex]
    const hoveredSection = activeItem.sections?.[hoveredSectionIndex]
    const hoveredSub =
      hoveredSubIndex !== null ? hoveredSection?.subsections?.[hoveredSubIndex] : null
    const hoveredSubSub =
      hoveredSubSubIndex !== null ? hoveredSub?.subSubsections?.[hoveredSubSubIndex] : null

    let image, description, title

    if (hoveredSubSubIndex !== null && hoveredSubSub) {
      image = hoveredSubSub?.subSubsectionImage?.thumbnailURL
      description = hoveredSubSub?.subSubsectionPara
      title = hoveredSubSub?.subSubsectionText
    } else if (hoveredSubIndex !== null && hoveredSub) {
      image = hoveredSub?.subsectionImage?.thumbnailURL
      description = hoveredSub?.subsectionPara
      title = hoveredSub?.subsectionText
    } else if (hoveredSection) {
      image = hoveredSection?.sectionImage?.thumbnailURL
      description = hoveredSection?.sectionPara
      title = hoveredSection?.sectionText
    } else {
      image = null
      description = ''
      title = ''
    }

    return { image, description, title }
  }

  const displayContent = getCurrentDisplayContent()
  console.log(displayContent)

  // Framer Motion Variants
  const listVariants = {
    visible: {
      transition: {
        staggerChildren: 0.07,
        delayChildren: 0.1,
      },
    },
    hidden: {},
  }

  const itemVariants = (isArabic: boolean) => ({
    hidden: {
      opacity: 0,
      x: isArabic ? 10 : -10,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.25,
        ease: easeOut,
      },
    },
  })

  return (
    <div onMouseLeave={handleNavLeave} >
      <nav className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 xl:gap-8 items-center z-[999] relative w-full">
        {data?.map((item: any, i: number) => {
          const url = item.link?.url
          const label = item.link?.label
          const isActive = pathname === url
          const hasHoverMenu = item.needHoverMenu && item.sections?.length > 0
          const isMenuActive = activeMenuIndex === i

          if (!url || !label) return null

          return (
            <div
              key={i}
              className="relative"
              onMouseEnter={() => handleNavItemEnter(i, hasHoverMenu)}
            >
              <Link href={url} onClick={resetHoverMenu}>
                <p
                  className={`${
                    isQuotePage
                      ? 'text-black'
                      : isActive || isMenuActive
                        ? 'text-[#ff5938]'
                        : 'text-white'
                  } hover:cursor-pointer relative inline-block text-[12.5px] sm:text-[13px] xl:text-[14px] font-light transition-colors duration-500 hover:text-[#ff5938] after:absolute after:left-1/2 after:bottom-0 after:-translate-x-1/2 ${
                    isActive || isMenuActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'
                  } after:h-[1px] after:bg-red-400 after:transition-all after:duration-300`}
                >
                  {label}
                </p>
              </Link>
            </div>
          )
        })}

        {/* Quote Button */}
        <button className="relative overflow-hidden group bg-[#EE3E25] hover:bg-transparent duration-1000 text-white text-[12px] sm:text-[13px] md:text-[14px] xl:text-[15.5px] px-4 sm:px-6 md:px-8 xl:px-[25px] py-1.5 sm:py-2 md:py-2.5 xl:py-3 whitespace-nowrap transition-all">
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-2 bg-white rounded-full z-0 opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-[25] transition-all duration-700 ease-in-out" />
          <Link href="/get-a-quote" onClick={resetHoverMenu}>
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#EE3E25]">
              {isArabic ? 'طلب عرض' : 'GET A QUOTE'}
            </span>
          </Link>
        </button>
      </nav>

      {/* Hover Menu */}
      <AnimatePresence>
        {activeMenuIndex !== null && data[activeMenuIndex]?.needHoverMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: easeOut }}
            className="absolute left-0 top-[20px] xl:top-[25px] z-[9998] w-screen max-w-none pointer-events-none"
            onMouseEnter={handleMenuEnter}
            onMouseLeave={handleMenuLeave}
          >
            <div className="h-[26px] w-full" />
            <div className="bg-white text-black shadow-xl w-full pointer-events-auto">
              {(() => {
                const activeItem = data[activeMenuIndex]
                const hoveredSection = activeItem.sections?.[hoveredSectionIndex]
                const hasSubsections = hoveredSection?.subsections?.length > 0

                return (
                  <div
                    dir={isArabic ? 'rtl' : 'ltr'}
                    className="max-w-[90%] mx-auto px-6 py-12 grid grid-cols-3 gap-8 h-[400px] mt-9"
                  >
                    {/* Sections */}
                    <div className="px-6 border-gray-300 border-r rtl:border-l rtl:border-r-0">
                      <ul className="space-y-6">
                        {activeItem.sections.map((section: any, sectionIndex: number) => (
                          <li
                            key={sectionIndex}
                            onMouseEnter={() => {
                              setHoveredSectionIndex(sectionIndex)
                              setHoveredSubIndex(null)
                              setHoveredSubSubIndex(null)
                            }}
                          >
                            <Link href={section.sectionLink || '#'} onClick={resetHoverMenu}>
                              <p
                                className={`
    relative inline-block text-[15px] font-normal uppercase transition-colors duration-300
    ${hoveredSectionIndex === sectionIndex ? 'text-[#ff5938]' : 'text-[#ee3e25]'}
    after:content-['']
    after:absolute
    after:bottom-0
    after:h-[1px]
    after:w-0
    after:bg-[#ff5938]
    after:transition-all
    after:duration-300
    after:left-0
    rtl:after:left-auto
    rtl:after:right-0
    hover:after:w-full
  `}
                              >
                                {section.sectionText || 'Untitled Section'}
                              </p>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Subsections */}
                    <div
                      className={`px-6 ${hasSubsections ? 'border-gray-300 border-r rtl:border-l rtl:border-r-0' : ''}`}
                    >
                      {hasSubsections ? (
                        <motion.ul
                          key={hoveredSectionIndex} // ✅ forces re-animation on hover
                          initial="hidden"
                          animate="visible"
                          variants={listVariants}
                          className={`space-y-2 ${isArabic ? 'rtl:mr-2' : 'ml-2'}`}
                        >
                          {hoveredSection.subsections.map((sub: any, subIndex: number) => (
                            <motion.li
                              key={subIndex}
                              variants={itemVariants(isArabic)}
                              onMouseEnter={() => {
                                setHoveredSubIndex(subIndex)
                                setHoveredSubSubIndex(null)
                              }}
                            >
                              <Link href={sub.subsectionLink || '#'} onClick={resetHoverMenu}>
                                <p className="text-[15px] hover:text-[#ff5938] font-light mb-2 text-left rtl:text-right capitalize">
                                  {sub.subsectionText || 'Subservice'}
                                </p>
                              </Link>

                              {sub.subSubsections?.length > 0 && (
                                <motion.ul
                                  initial="hidden"
                                  animate="visible"
                                  variants={listVariants}
                                  className={`mt-1 space-y-2 ${isArabic ? 'rtl:mr-4' : 'ml-4'}`}
                                >
                                  {sub.subSubsections.map((subsub: any, subsubIndex: number) => (
                                    <motion.li
                                      key={subsubIndex}
                                      variants={itemVariants(isArabic)}
                                      className="flex items-center gap-1"
                                      onMouseEnter={() => setHoveredSubSubIndex(subsubIndex)}
                                    >
                                      {isArabic ? (
                                        <>
                                          <ChevronRight
                                            size={14}
                                            className="text-gray-600 rotate-180"
                                          />
                                          <Link
                                            href={subsub.subSubsectionLink || '#'}
                                            onClick={resetHoverMenu}
                                          >
                                            <p className="text-[13px] text-gray-600 hover:text-[#ff5938] text-right capitalize">
                                              {subsub.subSubsectionText || 'Sub-subservice'}
                                            </p>
                                          </Link>
                                        </>
                                      ) : (
                                        <>
                                          <ChevronRight size={14} className="text-gray-600" />
                                          <Link
                                            href={subsub.subSubsectionLink || '#'}
                                            onClick={resetHoverMenu}
                                          >
                                            <p className="text-[13px] text-gray-600 hover:text-[#ff5938] text-left capitalize">
                                              {subsub.subSubsectionText || 'Sub-subservice'}
                                            </p>
                                          </Link>
                                        </>
                                      )}
                                    </motion.li>
                                  ))}
                                </motion.ul>
                              )}
                            </motion.li>
                          ))}
                        </motion.ul>
                      ) : (
                        <div className="h-full" />
                      )}
                    </div>

                    {/* Image + Description */}
                    <div className="px-6 rtl:pl-0 rtl:pr-6">
                      {displayContent?.image ? (
                        <div className="w-full h-[250px] overflow-hidden relative">
                          <AnimatePresence mode="wait">
                            <motion.img
                              key={displayContent.image} // key change triggers animation
                              src={displayContent.image}
                              alt={displayContent.title || ''}
                              className="object-cover w-full h-full absolute top-0 left-0"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.1 }}
                            />
                          </AnimatePresence>
                        </div>
                      ) : (
                        <div className="w-full h-[250px]" />
                      )}

                      <AnimatePresence mode="wait">
                        {displayContent?.description && (
                          <motion.p
                            key={displayContent.description}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4, ease: 'easeOut' }}
                            className="text-[13px] text-gray-700 mt-3 leading-snug"
                          >
                            {displayContent.description}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
